import React from 'react';
import { ChevronDown, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isValidPassword } from '../../../shared/password-rules.js';

export const validateInput = (value: string | string[], type: string): boolean => {
  // Handle multiselect (array values)
  if (Array.isArray(value)) {
    return type === 'multiselect' ? value.length > 0 : false;
  }

  // Handle string values
  if (value.trim() === '') return false;
  
  switch (type) {
    case 'email':
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    case 'password':
      // The same rule as the server's, see shared/password-rules.ts.
      return isValidPassword(value);
    case 'text':
    default:
      return value.trim() !== '';
  }
};

const PASSWORD_REQUIREMENTS_FALLBACK =
  'Mindestens 8 Zeichen, mit Groß- und Kleinbuchstaben, einer Ziffer und einem Sonderzeichen.';

interface LabeledInputProps {
  id: string;
  name: string;
  label: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  showValidation?: boolean;
  /** Shown under the field while it has no error, e.g. the password requirements. */
  hint?: string;
  className?: string;
}

const getErrorMessage = (type: string, isEmpty: boolean): string => {
  if (isEmpty) {
    return 'This field is required';
  }
  
  switch (type) {
    case 'email':
      return 'Invalid email address';
    case 'text':
    default:
      return 'Invalid input';
  }
};

export default function LabeledInput({
  id,
  name,
  label,
  type = 'text',
  value,
  onChange,
  placeholder = '',
  required = false,
  showValidation = false,
  hint,
  className = '',
}: LabeledInputProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = React.useState(false);
  const isPasswordField = type === 'password';
  const inputType = isPasswordField && showPassword ? 'text' : type;
  
  // Check if field is empty
  const isEmpty = value.trim() === '';
  
  // Compute error based on type-specific validation
  // Show error if: (required AND invalid) OR (not required AND not empty AND invalid)
  const hasError = showValidation && (
    (required && !validateInput(value, type)) ||
    (!required && !isEmpty && !validateInput(value, type))
  );

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <input
          type={inputType}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-secondary text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:border-transparent ${
            hasError ? 'border-red-400 focus:ring-red-200' : 'border-slate-200 dark:border-slate-700 focus:ring-brand-500'
          } ${isPasswordField ? 'pr-12' : ''} ${className}`}
          aria-invalid={hasError}
        />
        {isPasswordField && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        )}
      </div>
      {hasError && (
        <p className="mt-1 text-xs text-red-500">
          {!isEmpty && type === 'password'
            ? t('AuthModal.passwordRequirements', { defaultValue: PASSWORD_REQUIREMENTS_FALLBACK })
            : getErrorMessage(type, isEmpty)}
        </p>
      )}
      {!hasError && hint && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
    </div>
  );
}

interface MultiSelectDropdownProps<T extends string> {
  id: string;
  label: string;
  options: readonly T[];
  selectedValues: T[];
  onToggle: (value: T) => void;
  required?: boolean;
  showValidation?: boolean;
  placeholder?: string;
  formatOption?: (value: T) => string;
}

export function MultiSelectDropdown<T extends string>({
  id,
  label,
  options,
  selectedValues,
  onToggle,
  required = false,
  showValidation = false,
  placeholder = 'Select options',
  formatOption = (value) => value[0].toUpperCase() + value.slice(1),
}: MultiSelectDropdownProps<T>) {
  const [isOpen, setIsOpen] = React.useState(false);
  const hasError = showValidation && required && !validateInput(selectedValues, 'multiselect');

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <div
          id={id}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-invalid={hasError}
          className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-secondary text-slate-900 dark:text-slate-100 cursor-pointer ${
            hasError ? 'border-red-400' : 'border-slate-200 dark:border-slate-700'
          }`}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          {selectedValues.length > 0
            ? selectedValues.map(formatOption).join(', ')
            : <span className="text-slate-400 dark:text-slate-500">{placeholder}</span>}
        </div>
        <button
          type="button"
          aria-label={`Toggle ${label}`}
          onClick={() => setIsOpen((prev) => !prev)}
          className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
        >
          <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div
            role="listbox"
            aria-multiselectable="true"
            className="absolute z-10 mt-2 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-popover shadow-lg max-h-56 overflow-auto"
          >
            {options.map((option) => (
              <label
                key={option}
                className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={() => onToggle(option)}
                  className="h-4 w-4 text-brand-500 border-slate-300 rounded"
                />
                {formatOption(option)}
              </label>
            ))}
          </div>
        )}
      </div>
      {hasError && (
        <p className="mt-1 text-xs text-red-500">
          Please select at least one option
        </p>
      )}
    </div>
  );
}
